import {
    CMM_JUDGE_SCORER_ID,
    CONVERSATIONAL_MM_AGENT,
} from '@fruitful/llm-service-shared';
import { CONVERSATIONAL_QA_TEST_CASES } from '@fruitful/llm-service-shared/qa';
import { type StorageThreadType } from '@mastra/core/memory';
import { useRouter } from 'expo-router';
import { Column } from 'primereact/column';
import { DataTable, type DataTableRowClickEvent } from 'primereact/datatable';
import { type ReactNode, useMemo } from 'react';
import {
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { MutedTableCell } from '../../common/components/GridCellRenderers';
import { ScorerValueWithRubric } from '../components/ScorerValueWithRubric';
import { getThreadScorerScore } from '../utils/qaThreadScorers';
import { formatScorerValue, getScorerValueColor } from '../utils/scoreUtils';
import {
    BRANCH_LABELS,
    type QASimulationThreadMetadata,
    toSimulationThreadMetadata,
} from './qaSimulationThreads';
import { useQASimulationThreads } from './useQASimulationThreads';

const TEST_CASE_BY_ID = new Map(
    CONVERSATIONAL_QA_TEST_CASES.map((testCase) => [testCase.id, testCase]),
);

const formatDate = (value: Date | string): string =>
    Number.isNaN(new Date(value).getTime())
        ? String(value)
        : new Date(value).toLocaleString();

interface HistoryRow {
    threadId: string;
    updatedAt: Date | string;
    metadata: QASimulationThreadMetadata | null;
}

const hasThreadId = (value: unknown): value is { threadId: string } => {
    return (
        value != null &&
        typeof value === 'object' &&
        'threadId' in value &&
        typeof value.threadId === 'string'
    );
};

const renderUpdated = ({ updatedAt }: HistoryRow): ReactNode => (
    <BodySmallRegular>{formatDate(updatedAt)}</BodySmallRegular>
);

const renderCase = ({ metadata }: HistoryRow): ReactNode => {
    const testCaseId = metadata?.qaTestCaseId;
    if (testCaseId == null) return <MutedTableCell />;

    const testCase = TEST_CASE_BY_ID.get(testCaseId);
    return (
        <BodySmallRegular color="$blue900" fontWeight="700">
            {testCase?.name ?? testCaseId}
        </BodySmallRegular>
    );
};

const renderPath = ({ metadata }: HistoryRow): ReactNode => {
    const testCase =
        metadata?.qaTestCaseId != null
            ? TEST_CASE_BY_ID.get(metadata.qaTestCaseId)
            : undefined;
    return testCase != null ? (
        <BodySmallRegular>{testCase.path}</BodySmallRegular>
    ) : (
        <MutedTableCell />
    );
};

const renderPrompt = ({ metadata }: HistoryRow): ReactNode => {
    const promptName = metadata?.promptName;
    return promptName != null && promptName !== '' ? (
        <BodySmallRegular>{promptName}</BodySmallRegular>
    ) : (
        <BodySmallRegular color="$gray700">Default</BodySmallRegular>
    );
};

const renderTurns = ({ metadata }: HistoryRow): ReactNode => {
    const metrics = metadata?.metrics;
    if (metrics == null) return <MutedTableCell />;
    return (
        <BodySmallRegular>
            {String(metrics.participantTurns + metrics.agentTurns)}
        </BodySmallRegular>
    );
};

const renderBranch = ({ metadata }: HistoryRow): ReactNode => {
    const branch = metadata?.metrics?.branch;
    if (branch == null) return <MutedTableCell />;
    return (
        <BodySmallRegular color="$gray800">
            {BRANCH_LABELS[branch] ?? branch}
        </BodySmallRegular>
    );
};

const renderScore = ({ metadata }: HistoryRow): ReactNode => {
    const score = getThreadScorerScore(metadata?.scorers, CMM_JUDGE_SCORER_ID);
    if (score == null) return <MutedTableCell />;
    return (
        <ScorerValueWithRubric score={score} scorerId={CMM_JUDGE_SCORER_ID}>
            <BodySmallRegular
                color={getScorerValueColor({
                    score,
                    scorerId: CMM_JUDGE_SCORER_ID,
                })}
                fontWeight="700"
            >
                {formatScorerValue({ score, scorerId: CMM_JUDGE_SCORER_ID })}
            </BodySmallRegular>
        </ScorerValueWithRubric>
    );
};

export default function ConversationalOnboardingQAHistoryPage() {
    const router = useRouter();
    const { data: threads = [], isLoading, error } = useQASimulationThreads();

    const rows: HistoryRow[] = useMemo(
        () =>
            threads.map((thread: StorageThreadType) => ({
                threadId: thread.id,
                updatedAt: thread.updatedAt,
                metadata: toSimulationThreadMetadata(thread.metadata),
            })),
        [threads],
    );

    if (isLoading) {
        return (
            <XStack justifyContent="center" padding="$4">
                <Spinner />
            </XStack>
        );
    }

    if (error != null) {
        return (
            <YStack padding="$4">
                <BodySmallRegular color="$red1100">
                    Unable to load simulation history.
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <YStack
            flex={1}
            minHeight={0}
            height="100%"
            width="100%"
            gap={12}
            padding={8}
            overflow="hidden"
        >
            <XStack alignItems="center" gap={8}>
                <H6Emphasized>Simulation history</H6Emphasized>
                <BodySmallRegular color="$gray700">
                    {String(rows.length)}
                </BodySmallRegular>
            </XStack>
            <YStack flex={1} minHeight={0}>
                <DataTable
                    value={rows}
                    dataKey="threadId"
                    size="small"
                    showGridlines
                    stripedRows
                    scrollable
                    scrollHeight="flex"
                    tableStyle={{ width: '100%' }}
                    emptyMessage="No simulation runs yet."
                    rowClassName={() => 'qa-history-row'}
                    onRowClick={(event: DataTableRowClickEvent) => {
                        const rowData: unknown = event.data;
                        if (!hasThreadId(rowData)) {
                            return;
                        }

                        router.push({
                            pathname: '/ai/threads/[threadId]',
                            params: {
                                threadId: rowData.threadId,
                                agentId: CONVERSATIONAL_MM_AGENT.id,
                            },
                        });
                    }}
                >
                    <Column
                        field="updatedAt"
                        header="Run"
                        body={renderUpdated}
                        style={{ width: '20%' }}
                    />
                    <Column
                        field="case"
                        header="Case"
                        body={renderCase}
                        style={{ width: '24%' }}
                    />
                    <Column
                        field="path"
                        header="Path"
                        body={renderPath}
                        style={{ width: '10%' }}
                    />
                    <Column
                        field="prompt"
                        header="Prompt"
                        body={renderPrompt}
                        style={{ width: '16%' }}
                    />
                    <Column
                        field="turns"
                        header="Turns"
                        body={renderTurns}
                        alignHeader="center"
                        style={{ width: '8%', textAlign: 'center' }}
                    />
                    <Column
                        field="branch"
                        header="Branch"
                        body={renderBranch}
                        style={{ width: '14%' }}
                    />
                    <Column
                        field="scorers"
                        header="Score"
                        body={renderScore}
                        alignHeader="center"
                        style={{ width: '8%', textAlign: 'center' }}
                    />
                </DataTable>
            </YStack>
            <style>{`
                .p-datatable .qa-history-row {
                    cursor: pointer;
                }
                .p-datatable .qa-history-row:hover {
                    background-color: #E1FDEA !important;
                }
            `}</style>
        </YStack>
    );
}
